import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import DramaCard from "@/components/base/DramaCard";
import FilterGroup from "@/pages/category/components/FilterGroup";
import type { DramaCategory, DramaEra, DramaRegion } from "@/types";

const PAGE_SIZE = 6;

const eraOptions = [
  { value: "全部", label: "全部年代" },
  { value: "2005-2010", label: "2005-2010" },
  { value: "2011至今", label: "2011 至今" },
];

const catOptions = [
  { value: "全部", label: "全部类型" },
  { value: "正传", label: "仙剑正传" },
  { value: "衍生", label: "周边衍生" },
  { value: "相关仙侠", label: "相关仙侠" },
];

const regionOptions = [
  { value: "全部", label: "全部地区" },
  { value: "大陆", label: "大陆" },
  { value: "港台", label: "港台" },
];

const sortOptions = [
  { value: "heat", label: "按热度" },
  { value: "year", label: "按首播时间" },
  { value: "rating", label: "按评分" },
];

export default function Category() {
  const [params, setParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  usePageMeta(
    "仙剑奇侠传电视剧分类 - 影视剧集大全",
    "仙剑奇侠传电视剧分类大全，支持按年代、类型、地区多维筛选，并按热度、首播时间、评分排序，快速找到感兴趣的经典仙侠剧集。",
    "仙剑奇侠传电视剧分类,仙侠剧大全,仙剑剧集列表,仙侠电视剧筛选"
  );

  const keyword = params.get("q") || "";
  const cat = params.get("cat") || "全部";
  const era = params.get("era") || "全部";
  const region = params.get("region") || "全部";
  const sort = params.get("sort") || "heat";
  const rawPage = Number.parseInt(params.get("page") || "1", 10);
  const page = Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  const resetFilters = () => {
    setParams(new URLSearchParams());
  };

  const filtered = useMemo(() => {
    const list = dramas.filter((drama) => {
      if (cat !== "全部" && drama.category !== (cat as DramaCategory)) return false;
      if (era !== "全部" && drama.era !== (era as DramaEra)) return false;
      if (region !== "全部" && drama.region !== (region as DramaRegion)) return false;
      if (keyword) {
        const hitTitle = drama.title.includes(keyword);
        const hitCharacter = drama.characters.some(
          (character) => character.name.includes(keyword) || character.actor.includes(keyword)
        );
        if (!hitTitle && !hitCharacter) return false;
      }
      return true;
    });

    const sorted = [...list];
    if (sort === "year") {
      sorted.sort((a, b) => b.year - a.year);
    } else if (sort === "rating") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => b.heat - a.heat);
    }
    return sorted;
  }, [cat, era, region, keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hasActiveFilter =
    Boolean(keyword) || cat !== "全部" || era !== "全部" || region !== "全部";

  const filterContent = (
    <div className="flex flex-col gap-6">
      <FilterGroup
        label="剧集年代"
        options={eraOptions}
        value={era}
        onChange={(value) => updateParam("era", value)}
      />
      <FilterGroup
        label="副分类"
        options={catOptions}
        value={cat}
        onChange={(value) => updateParam("cat", value)}
      />
      <FilterGroup
        label="地区"
        options={regionOptions}
        value={region}
        onChange={(value) => updateParam("region", value)}
      />
      <FilterGroup
        label="排序方式"
        options={sortOptions}
        value={sort}
        onChange={(value) => updateParam("sort", value)}
      />
      {hasActiveFilter && (
        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex items-center justify-center gap-2 h-10 rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700 text-sm whitespace-nowrap transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          重置全部筛选
        </button>
      )}
    </div>
  );

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "电视剧分类" }]} />
      </div>

      <section className="w-full px-4 md:px-8 pt-6 pb-4 md:pb-6">
        <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
          仙剑奇侠传电视剧分类
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          汇总仙剑奇侠传正传、周边衍生作品及相关经典仙侠剧。你可以按年代、副分类与地区进行组合筛选，
          并按热度、首播时间或评分排序，快速定位想看的剧集。
        </p>
      </section>

      <div className="w-full px-4 md:px-8 pb-12 md:pb-16 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 md:gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-700"></i>
              筛选与排序
            </h2>
            {filterContent}
          </div>
        </aside>

        <div className="min-w-0">
          <div className="lg:hidden mb-4">
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md border border-background-300 bg-background-50 text-foreground-800 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              筛选与排序
              <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
            {filtersOpen && (
              <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-5 animate-fade-in">
                {filterContent}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              共找到 <span className="text-primary-700 font-medium">{filtered.length}</span> 部剧集
              {keyword && (
                <>
                  ，关键词「<span className="text-foreground-900">{keyword}</span>」
                </>
              )}
            </p>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="lg:hidden inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line"></i>
                清除筛选
              </button>
            )}
          </div>

          {pagedList.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                {pagedList.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
              <div className="mt-10">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={(nextPage) => updateParam("page", String(nextPage))}
                />
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 px-6 text-center">
              <span className="mx-auto w-16 h-16 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-3xl">
                <i className="ri-search-eye-line"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg text-foreground-950">没有找到匹配的剧集</h3>
              <p className="mt-2 text-sm text-foreground-500">
                试试更换筛选条件或关键词，或者重置筛选查看全部剧集。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 h-10 px-6 rounded-md bg-primary-700 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}